import React, { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { glyphs, frames, colors, css, useMediaQuery, mediaQueriesNumbers, fonts } from "../../../Styles";
import { NewInput, PageHeader, AddNewUserModal, DeleteUserModal, TableLayout, LoadingSpinner, NewButtonList, SecondaryPopover, ReactiveTable } from "../../../Components";
import { ButtonTypes, ListData_ValueTypes, NewFieldStyles, NewInputHandles, TableStyles_Types } from "../../../Definitions";
import { useQuery } from "@apollo/client";
import { AllUser_Data, ALLUSER_INFO, AllUserInfoResponse } from "../../../Services/Agrisys_GqlDataCaller/UserCalls/GQLCalls/userCalls";
import { ColumnFiltersState, FilterFn, getCoreRowModel, getFilteredRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";
import { useColumnConstructor } from "../../../Components/Tables/_Tables/useColumnConstructor";

type UserListViewBlockProps = {
  onView: (userId: string) => void;
};

const user_groups = {
  nameMap: {
    free: "Owner",
    worker: "Worker",
  } as Record<string, string>,
};

// function getNameOptions() {
//   return Object.entries(user_groups.nameMap).map(([key, value]) => ({ label: value, id: key }));
// }

export default function Users({ onView }: UserListViewBlockProps) {
  localStorage.setItem("activedomain", "");
  const { t } = useTranslation();
  const mobileXs = useMediaQuery(`(max-width: ${mediaQueriesNumbers.mobileXs}px)`); 
  const searchRef = useRef<NewInputHandles>(null);
  const { data, loading, error, refetch} = useQuery<AllUserInfoResponse>(ALLUSER_INFO);
  const [ globalFilterValue, setGlobalFilterValue ] = useState<string>("");
  const [ columnFilters, setColumnFilters ] = useState<ColumnFiltersState>([]);
  const [ companyGroupAddModal, setCompanyGroupAddModal ] = useState<boolean>(false);
  const [ userDeleteModal, setUserDeleteModal ] = useState<boolean>(false);
    
  const _labelStyle = {...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary, ...fonts.fontWeight2, whiteSpace: "nowrap", textOverflow: "ellipsis", maxWidth: "94px", minHeight: "15px", overflow: "hidden"};
  const _valueStyle = { ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary, minHeight: "15px" };
  
  // #region | Table Data
  const tableData = useMemo(() => {
    if (!data || !data.user?.allUsers) return [];
  
    return data.user.allUsers.map((user: AllUser_Data) => {
      const sortedCompanies = user.user_companies?.slice().sort((a, b) => a.name.localeCompare(b.name)) ?? [];
      const companyData = sortedCompanies.flatMap(company => user.user_groups?.filter(c => c.fk_company === company.company_id).map(c => ({ companyName: company.name, userGroup: user_groups.nameMap[c.name] ?? c.name })) ?? []);
      const companyGroupsText = companyData.length > 0 ? companyData.map(c => `${c.companyName}: ${c.userGroup}`).join(" ") : "No groups";
      let companyGroupsCell: React.ReactNode;
  
      if (companyData.length <= 1) {
        companyGroupsCell = (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>
              {companyData.length === 1
                ? `${companyData[0].companyName}: ${companyData[0].userGroup}`
                : "No groups"}
            </span>
          </div>
        );
      } else {
        companyGroupsCell = (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>{`${companyData[0].companyName}: ${companyData[0].userGroup}`}</span>
  
            <SecondaryPopover
              content={<glyphs.Note fillColor={colors.greyDarkPrimary} />}
              popoverHeader={"Company Groups"}
              popoverContent={<>
                  <div style={{ display: "grid", gridTemplateColumns: "94px 2px 94px", gap: "10px", width: "100%", marginBottom: "6px" }}>
                    <div style={{ display: "flex", justifyContent: "flex-end", ..._labelStyle }}> Group </div>
                    <div style={{ width: "2px", backgroundColor: colors.greyDarkSecondary, opacity: 0.5 }}/>
                    <div style={{ display: "flex", justifyContent: "flex-start", ..._labelStyle }}> Role </div>
                  </div>
                  {companyData.map((company, index) => (
                    <div key={index} style={{ display: "grid", gridTemplateColumns: "94px 2px 94px", gap: "10px", width: "100%", marginBottom: "4px" }}>
                      <div style={{ display: "flex", justifyContent: "flex-end", ..._valueStyle }}> {company.companyName} </div>
                      <div style={{ width: "2px", backgroundColor: colors.greyDarkSecondary, opacity: 0.5 }}/>
                      <div style={{ display: "flex", justifyContent: "flex-start", ..._valueStyle }}> {company.userGroup} </div>
                    </div>
                  ))}
                </>
              }
            />
          </div>
        );
      }
  
      return {
        id: user.user_id,
        user_id: user.user_id,
        name: `${user.firstname || ""} ${user.lastname || ""}`,
        companyGroups: companyGroupsCell,
        companyGroupsText,
        email: user.email || "",
        address: user.address || "",
        phone: user.phone || "",
        username: user.username || ""
      };
    });
  }, [data]);

  // #endregion

  // #region | Table Construction
  const columnsData = useMemo(() => [
    { headerValue: "ID", accessor: "id", type: ListData_ValueTypes.String, sort: true },
    { headerValue: "Username", accessor: "username", type: ListData_ValueTypes.String, sort: true },
    { headerValue: "Company & Groups", accessor: "companyGroups", type: ListData_ValueTypes.Div, sort: false },
    { headerValue: "Name", accessor: "name", type: ListData_ValueTypes.String, sort: true },
    { headerValue: "Email", accessor: "email", type: ListData_ValueTypes.String, sort: true },
    { headerValue: "Address", accessor: "address", type: ListData_ValueTypes.String, sort: true },
    { headerValue: "Phone", accessor: "phone", type: ListData_ValueTypes.String, sort: true },
    { headerValue: "CompanyGroupsText", accessor: "companyGroupsText", type: ListData_ValueTypes.String, hidden: true, sort: false },
  ], [onView]);

  const columns = useColumnConstructor({
    accessorData: columnsData,
    tableStyle: TableStyles_Types.primaryTable,
    canEdit: false
  });

  const globalFilterFn: FilterFn<any> = (row, _columnId, filterValue) => {
  const search = filterValue.toLowerCase();

  return row.getAllCells().some(cell => {
    const value = cell.getValue();
    return typeof value === "string" && value.toLowerCase().includes(search);
  })};
  
  const table = useReactTable({
    columns,
    data: tableData,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnFiltersChange: setColumnFilters,
    globalFilterFn,
    onGlobalFilterChange: setGlobalFilterValue,
    state: {
      globalFilter: globalFilterValue,
      columnFilters,
      columnVisibility: { companyGroupsText: false },
    },
    initialState: {
      sorting: [{ id: "username", desc: false }], 
    },
  });
  // #endregion

  return ( <>
    <frames.Page_inner>
        <PageHeader topHeader="Users" subHeader="Overview" />
      <frames._page_inner_content>
        <TableLayout
          fillScreenTable
          searchbBarArea={
            <NewInput
              ref={searchRef}
              onChange={(value: string) => setGlobalFilterValue(value)}
              fieldOverrides={{
                fieldStyle: NewFieldStyles.greySecondary,
                fieldWidth: mobileXs ? "150px" : undefined
              }}
              placeholder={t("search", { defaultValue: "Search" })}
            />
          }
          buttonArea={
            <NewButtonList
              contents={[
                "add",
                "delete",
              ]}
              onClick={(i) => {
                if (i === 0) setCompanyGroupAddModal(true);
                if (i === 1) setUserDeleteModal(true);
              }}
              buttonType={ButtonTypes.tableButton}
              startEnhancers={[glyphs.Add({}), glyphs.Delete({})]}
            />
          }
          tableArea={
            loading ?
              <LoadingSpinner/>
              : error ? <div style={{ ...css.emptyPen, textAlign: "center", padding: "20px" }}>Error loading users</div>
              : <ReactiveTable
                  table={table}
                  tableType={TableStyles_Types.primaryTable}
                  emptyText="No users found"
                  renderFooter={false}
                />
            }
        />
      </frames._page_inner_content>
    </frames.Page_inner>

    <AddNewUserModal
      isModalOpen={companyGroupAddModal}
      closeModal={() => setCompanyGroupAddModal(false)}
      userlist={data?.user?.allUsers.map(u => ({ ...u, firstname: u.firstname ?? "", lastname: u.lastname ?? "" })) ?? []}
      onUserAdded={() => refetch()}
    />
    
    <DeleteUserModal
      isModalOpen={userDeleteModal}
      closeModal={() => setUserDeleteModal(false)}
      userlist={data?.user?.allUsers.map(u => ({ ...u, firstname: u.firstname ?? "", lastname: u.lastname ?? "" })) ?? []}
      onUserDeleted={() => refetch()}
    />
  </> );
}


  // const tableData = useMemo(() => {
  //   if (!data || !data.user?.allUsers) return []; // fallback

  //   return data.user.allUsers.map((user: AllUser_Data) => {
  //     const companyGroups = user.user_companies?.slice().sort((a, b) => a.name.localeCompare(b.name)).map(company => {
  //       const groupsForCompany = user.user_groups?.filter(g => g.fk_company === company.company_id).map(g => user_groups.nameMap[g.name] ?? g.name);

  //       return `${company.name}: ${groupsForCompany && groupsForCompany.length > 0 ? groupsForCompany.join(", ") : "No groups"}`}) || [];
  //       let companyGroupsCell: React.ReactNode;
      
  //       if (companyGroups.length <= 1) {
  //         companyGroupsCell = (
  //           <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
  //             <span>{companyGroups[0] || "No groups"}</span>
  //           </div>
  //         );
  //       } else {
  //         companyGroupsCell = (
  //           <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
  //             <span>{companyGroups[0]}</span>
  //                 <SecondaryPopover
  //                   content={<glyphs.Note fillColor={colors.greyDarkPrimary}/>}
  //                   popoverHeader={"Company Groups"}
  //                   popoverContent={
  //                     <div style={{display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "center",  color: colors.whitePrimary}}>
  //                       {companyGroups.join("\n")?.split("\n").map((line, index) => (
  //                         <div key={index}>{line}</div>
  //                       ))}
  //                     </div>
  //                   }
  //                 />
  //           </div>
  //         );
  //       }
      
  //       return {
  //         id: user.user_id,
  //         user_id: user.user_id,
  //         name: (user.firstname || "") + " " + (user.lastname || ""),
  //         companyGroups: companyGroupsCell,
  //         companyGroupsText: companyGroups.join(" "),
  //         email: user.email || "",
  //         address: user.address || "",
  //         phone: user.phone || "",
  //         username: user.username || "",
  //       };
  //   });
// }, [data]);
  

  // popoverContent=
  //   {userGroupNote?.split("\n").map((line, index) => (
  //     <div key={index} style={{ display: "grid", gridTemplateColumns: "94px 2px 94px", gridTemplateRows: "auto", gap: "10px", width: "100%" }}>
  //       <div style={{ display: "flex", alignItems: "start", justifyContent: "end", ..._valueStyle }}> {dayComponent.componentId} </div>
  //       <div style={{ width: "2px", height: "100%", backgroundColor: colors.greyDarkSecondary, opacity: "0.5" }} />
  //       <div style={{ display: "flex", alignItems: "start", justifyContent: "start", height: "18px", ..._valueStyle }}>{dayComponent.amount}</div>
  //     </div>
  //   ))}